import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_LANDING_PAGE_CONTENT,
  normalizeLandingPageContent,
} from "../data/landingPageCmsDefaults";
import { getLandingPageContentPublicBySlug } from "../services/landingPageCmsApi";

function trackLandingEvent(eventName, properties = {}) {
  if (!eventName) return;

  const safeProperties = Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [key, String(value ?? "")])
  );

  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", eventName, safeProperties);
    }
  } catch (_err) {}

  try {
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: "landing_event",
        event_name: eventName,
        ...safeProperties,
      });
    }
  } catch (_err) {}

  try {
    if (typeof window.plausible === "function") {
      window.plausible(eventName, { props: safeProperties });
    }
  } catch (_err) {}

  try {
    window.dispatchEvent(
      new CustomEvent("logboek24:landing-event", {
        detail: {
          event: eventName,
          properties: safeProperties,
          ts: Date.now(),
          path: window.location?.pathname || "/",
        },
      })
    );
  } catch (_err) {}
}

function useLandingSeo(content) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = content?.seo?.title || DEFAULT_LANDING_PAGE_CONTENT.seo.title;

    const updates = [
      {
        key: "name",
        value: "description",
        content: content?.seo?.description || DEFAULT_LANDING_PAGE_CONTENT.seo.description,
      },
      {
        key: "property",
        value: "og:title",
        content: content?.seo?.ogTitle || DEFAULT_LANDING_PAGE_CONTENT.seo.ogTitle,
      },
      {
        key: "property",
        value: "og:description",
        content: content?.seo?.ogDescription || DEFAULT_LANDING_PAGE_CONTENT.seo.ogDescription,
      },
    ];

    const created = [];
    for (const update of updates) {
      let element = document.head.querySelector(`meta[${update.key}="${update.value}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(update.key, update.value);
        document.head.appendChild(element);
        created.push(element);
      }
      element.setAttribute("content", update.content);
    }

    return () => {
      document.title = previousTitle;
      for (const element of created) {
        element.parentNode?.removeChild(element);
      }
    };
  }, [content]);
}

function TopNav({ navigation, accentColor, onTrack }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-800/90 bg-slate-950/90 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
        <a href="/" className="flex items-center gap-3" aria-label="Logboek24 home">
          <div
            className="rounded-xl px-2.5 py-1 text-sm font-semibold text-white"
            style={{ backgroundColor: accentColor }}
          >
            LOGBOEK24
          </div>
          <div className="hidden text-xs uppercase tracking-[0.18em] text-slate-300 sm:block">
            Operationeel geheugen
          </div>
        </a>

        <nav className="hidden items-center gap-7 text-sm text-slate-200 md:flex">
          {(navigation.items || []).map((item) => (
            <a
              key={`${item.label}-${item.href}`}
              href={item.href}
              className="transition hover:text-white"
              onClick={() => onTrack("section_nav_click", { target: item.href })}
            >
              {item.label}
            </a>
          ))}
          <a
            href={navigation.loginHref}
            className="rounded-xl border border-slate-700 px-4 py-2 font-medium transition hover:border-slate-500 hover:bg-slate-900"
            onClick={() => onTrack("login_click_top", { location: "top_nav" })}
          >
            {navigation.loginLabel}
          </a>
          <a
            href={navigation.demoHref}
            className="rounded-xl px-4 py-2 font-semibold text-white transition"
            style={{ backgroundColor: accentColor }}
            onClick={() => onTrack("demo_click_top", { location: "top_nav" })}
          >
            {navigation.demoLabel}
          </a>
        </nav>

        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-700 text-slate-200 md:hidden"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          aria-label="Menu openen"
        >
          {menuOpen ? "X" : "M"}
        </button>
      </div>

      {menuOpen && (
        <nav id="mobile-menu" className="border-t border-slate-800 px-6 py-4 md:hidden">
          <div className="flex flex-col gap-3 text-sm text-slate-200">
            {(navigation.items || []).map((item) => (
              <a
                key={`${item.label}-${item.href}-mobile`}
                href={item.href}
                onClick={() => {
                  onTrack("section_nav_click", { target: item.href, location: "mobile_menu" });
                  setMenuOpen(false);
                }}
              >
                {item.label}
              </a>
            ))}
            <a
              href={navigation.loginHref}
              onClick={() => {
                onTrack("login_click_top", { location: "mobile_menu" });
                setMenuOpen(false);
              }}
            >
              {navigation.loginLabel}
            </a>
            <a
              href={navigation.demoHref}
              onClick={() => {
                onTrack("demo_click_top", { location: "mobile_menu" });
                setMenuOpen(false);
              }}
              className="rounded-xl px-4 py-2 text-center font-semibold text-white"
              style={{ backgroundColor: accentColor }}
            >
              {navigation.demoLabel}
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}

function SectionAccent({ color }) {
  return (
    <div className="mx-auto h-1 max-w-7xl rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />
  );
}

function HeroDashboardSection({ content, accentColor, sectionColor, onTrack }) {
  const hero = content.hero;
  const dashboard = content.dashboard;

  return (
    <section className="relative overflow-hidden bg-slate-950 pt-4">
      <SectionAccent color={sectionColor} />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(251,146,60,0.22),transparent_38%),radial-gradient(circle_at_85%_15%,rgba(59,130,246,0.26),transparent_36%)]" />
      <div className="relative mx-auto grid max-w-7xl gap-10 px-6 pb-16 pt-14 lg:grid-cols-[0.96fr_1.04fr] lg:gap-12 lg:px-8 lg:pb-20 lg:pt-20">
        <div className="rounded-[2rem] border border-slate-700/70 bg-slate-900/70 p-7 shadow-2xl shadow-black/30 lg:p-9">
          <p className="text-xs uppercase tracking-[0.22em]" style={{ color: accentColor }}>
            {hero.eyebrow}
          </p>
          <h1 className="mt-4 text-4xl font-semibold leading-tight text-slate-100 sm:text-5xl">
            {hero.title}
          </h1>
          <p className="mt-5 text-base leading-8 text-slate-300 sm:text-lg">{hero.description}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={hero.primaryCtaHref}
              className="rounded-xl px-5 py-3 text-sm font-semibold text-white transition"
              style={{ backgroundColor: accentColor }}
              onClick={() => onTrack("demo_click_top", { location: "hero_primary" })}
            >
              {hero.primaryCtaLabel}
            </a>
            <a
              href={hero.secondaryCtaHref}
              className="rounded-xl border border-slate-600 px-5 py-3 text-sm font-semibold text-slate-100 transition hover:border-slate-400 hover:bg-slate-800/60"
              onClick={() => onTrack("results_click_top", { location: "hero_secondary" })}
            >
              {hero.secondaryCtaLabel}
            </a>
          </div>
          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            {content.signals.map((signal) => (
              <SignalTile key={signal.title} title={signal.title} text={signal.text} />
            ))}
          </div>
        </div>

        <div className="rounded-[2rem] border border-slate-700/70 bg-slate-900/65 p-5 shadow-2xl shadow-black/20 sm:p-6">
          <div className="rounded-[1.5rem] border border-slate-700 bg-slate-950/90 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-slate-400">
                  {dashboard.overviewLabel}
                </div>
                <div className="mt-1 text-2xl font-semibold text-white">{dashboard.clusterName}</div>
              </div>
              <div
                className="rounded-lg px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white"
                style={{ backgroundColor: accentColor }}
              >
                {dashboard.statusLabel}
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {dashboard.kpis.map((item) => (
                <article key={`${item.label}-${item.meta}`} className="rounded-xl border border-slate-700 bg-slate-900/80 p-4">
                  <div className="text-sm text-slate-300">{item.label}</div>
                  <div className="mt-2 text-3xl font-semibold text-white">{item.value}</div>
                  <div className="mt-1 text-xs text-slate-400">{item.meta}</div>
                </article>
              ))}
            </div>

            <div className="mt-5 rounded-xl border border-slate-700 bg-slate-900/70 p-4">
              <div className="flex items-center justify-between text-sm text-slate-300">
                <span>{dashboard.flowLabel}</span>
                <span>{dashboard.flowPeriodLabel}</span>
              </div>
              <div className="mt-3 space-y-3">
                {dashboard.flowRows.map((row) => (
                  <FlowRow key={`${row.label}-${row.value}`} label={row.label} value={row.value} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SignalTile({ title, text }) {
  return (
    <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 px-4 py-4">
      <div className="text-sm font-semibold text-slate-100">{title}</div>
      <div className="mt-1.5 text-xs leading-6 text-slate-300">{text}</div>
    </div>
  );
}

function FlowRow({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/70 px-3 py-2 text-xs">
      <span className="text-slate-300">{label}</span>
      <span className="font-medium text-slate-400">{value}</span>
    </div>
  );
}

function WorkflowSection({ content, accentColor, sectionColor, onTrack }) {
  return (
    <section id="workflow" className="bg-white py-16 lg:py-20">
      <SectionAccent color={sectionColor} />
      <div className="mx-auto max-w-7xl px-6 pt-8 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: accentColor }}>
            {content.workflow.eyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            {content.workflow.title}
          </h2>
          <p className="mt-4 text-lg leading-8 text-slate-600">{content.workflow.description}</p>
        </div>
        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {content.workflow.steps.map((step) => (
            <article
              key={step.title}
              className="rounded-[1.7rem] border border-slate-200 bg-slate-50 p-6"
              onClick={() => onTrack("workflow_step_click", { step: step.title })}
            >
              <div
                className="inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white"
                style={{ backgroundColor: accentColor }}
              >
                {step.badge}
              </div>
              <h3 className="mt-4 text-xl font-semibold text-slate-900">{step.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{step.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CaseTimelineSection({ content, accentColor, sectionColor, onTrack }) {
  const timeline = useMemo(() => content.caseStudy.timeline || [], [content.caseStudy.timeline]);
  const [expandedCaseYear, setExpandedCaseYear] = useState(timeline[1]?.year || timeline[0]?.year || "");

  useEffect(() => {
    const initial = timeline[1]?.year || timeline[0]?.year || "";
    setExpandedCaseYear((prev) => (prev ? prev : initial));
  }, [timeline]);

  function toggleCase(item) {
    const nextYear = expandedCaseYear === item.year ? "" : item.year;
    setExpandedCaseYear(nextYear);
    if (nextYear) {
      onTrack("case_expand", { case_year: item.year, case_title: item.title });
    }
  }

  return (
    <section id="resultaten" className="bg-slate-100 py-16 lg:py-20">
      <SectionAccent color={sectionColor} />
      <div className="mx-auto max-w-7xl px-6 pt-8 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: accentColor }}>
              {content.caseStudy.eyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              {content.caseStudy.title}
            </h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">{content.caseStudy.description}</p>
          </div>
          <a
            href={content.caseStudy.ctaHref}
            className="rounded-xl px-5 py-3 text-sm font-semibold text-white transition"
            style={{ backgroundColor: accentColor }}
            onClick={() => onTrack("demo_click_mid", { location: "case_header" })}
          >
            {content.caseStudy.ctaLabel}
          </a>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {timeline.map((item) => {
            const isExpanded = expandedCaseYear === item.year;
            return (
              <article key={`${item.year}-${item.title}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="text-xs uppercase tracking-wide text-slate-500">{item.year}</div>
                <h3 className="mt-2 text-lg font-semibold text-slate-900">{item.title}</h3>
                <p className="mt-2 text-sm leading-7 text-slate-600">{item.body}</p>
                <span
                  className="mt-4 inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white"
                  style={{ backgroundColor: accentColor }}
                >
                  {item.tag}
                </span>
                <button
                  type="button"
                  className="mt-4 inline-flex rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                  onClick={() => toggleCase(item)}
                >
                  {isExpanded ? "Minder details" : "Meer details"}
                </button>
                {isExpanded ? (
                  <p className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-6 text-slate-600">
                    {item.detail}
                  </p>
                ) : null}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SectorSection({ content, accentColor, sectionColor, onTrack }) {
  return (
    <section id="sectoren" className="relative overflow-hidden bg-slate-950 py-16 lg:py-20">
      <SectionAccent color={sectionColor} />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(59,130,246,0.16),transparent_45%),radial-gradient(circle_at_85%_80%,rgba(251,146,60,0.18),transparent_42%)]" />
      <div className="relative mx-auto max-w-7xl px-6 pt-8 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: accentColor }}>
            {content.sectors.eyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-100 sm:text-4xl">
            {content.sectors.title}
          </h2>
          <p className="mt-4 text-lg leading-8 text-slate-300">{content.sectors.description}</p>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {content.sectors.cards.map((item) => (
            <button
              key={item.title}
              type="button"
              className="rounded-[1.6rem] border border-slate-700 bg-slate-900/70 p-6 text-left transition hover:border-slate-500 hover:bg-slate-900"
              onClick={() => onTrack("sector_click", { sector: item.title })}
            >
              <h3 className="text-xl font-semibold text-slate-100">{item.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-300">{item.body}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection({ content, accentColor, sectionColor }) {
  return (
    <section className="bg-white py-16 lg:py-20" id="testimonials">
      <SectionAccent color={sectionColor} />
      <div className="mx-auto max-w-7xl px-6 pt-8 lg:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: accentColor }}>
          {content.testimonials.eyebrow}
        </p>
        <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
          {content.testimonials.title}
        </h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {content.testimonials.items.map((item, index) => (
            <article key={`${item.name}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <p className="text-sm leading-7 text-slate-700">"{item.quote}"</p>
              <div className="mt-4 text-sm font-semibold text-slate-900">{item.name}</div>
              <div className="text-xs text-slate-500">{item.role}</div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection({ content, accentColor, sectionColor, onTrack }) {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="bg-slate-100 py-16 lg:py-20" id="faq">
      <SectionAccent color={sectionColor} />
      <div className="mx-auto max-w-4xl px-6 pt-8 lg:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: accentColor }}>
          {content.faq.eyebrow}
        </p>
        <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
          {content.faq.title}
        </h2>
        <div className="mt-8 space-y-3">
          {content.faq.items.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <article key={`${item.question}-${index}`} className="rounded-xl border border-slate-200 bg-white">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-4 py-3 text-left"
                  onClick={() => {
                    setOpenIndex(isOpen ? -1 : index);
                    onTrack("faq_toggle", { index: String(index), question: item.question });
                  }}
                >
                  <span className="text-sm font-semibold text-slate-900">{item.question}</span>
                  <span className="text-slate-500">{isOpen ? "-" : "+"}</span>
                </button>
                {isOpen ? (
                  <p className="border-t border-slate-200 px-4 py-3 text-sm leading-7 text-slate-600">{item.answer}</p>
                ) : null}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CustomBlocksSection({ content, accentColor, sectionColor, onTrack }) {
  if (!content.customBlocks?.length) return null;

  return (
    <section className="bg-white py-16 lg:py-20" id="custom-blocks">
      <SectionAccent color={sectionColor} />
      <div className="mx-auto max-w-7xl px-6 pt-8 lg:px-8">
        <div className="grid gap-5 md:grid-cols-2">
          {content.customBlocks.map((block, index) => (
            <article key={`${block.title}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <h3 className="text-xl font-semibold text-slate-900">{block.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{block.body}</p>
              {block.ctaLabel ? (
                <a
                  href={block.ctaHref}
                  className="mt-5 inline-flex rounded-xl px-4 py-2 text-sm font-semibold text-white"
                  style={{ backgroundColor: accentColor }}
                  onClick={() => onTrack("custom_block_click", { block: block.title })}
                >
                  {block.ctaLabel}
                </a>
              ) : null}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ content, accentColor, sectionColor, onTrack }) {
  return (
    <section className="bg-white py-16 lg:py-20">
      <SectionAccent color={sectionColor} />
      <div className="mx-auto max-w-7xl px-6 pt-8 lg:px-8">
        <div className="rounded-[2rem] border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-8 lg:p-10">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: accentColor }}>
              {content.finalCta.eyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              {content.finalCta.title}
            </h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">{content.finalCta.description}</p>
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={content.finalCta.primaryCtaHref}
              className="rounded-xl px-6 py-3 text-sm font-semibold text-white transition"
              style={{ backgroundColor: accentColor }}
              onClick={() => onTrack("demo_click_bottom", { location: "final_cta" })}
            >
              {content.finalCta.primaryCtaLabel}
            </a>
            <a
              href={content.finalCta.secondaryCtaHref}
              className="rounded-xl border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
              onClick={() => onTrack("login_click_bottom", { location: "final_cta" })}
            >
              {content.finalCta.secondaryCtaLabel}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function LandingFooter({ footer }) {
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-6 py-8 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between lg:px-8">
        <div>© {new Date().getFullYear()} Logboek24 - {footer.copyright}</div>
        <div className="flex gap-5">
          {(footer.links || []).map((item) => (
            <a key={`${item.label}-${item.href}`} href={item.href} className="transition hover:text-slate-900">
              {item.label}
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
}

export default function Logboek24LandingPage({ slug = "home" }) {
  const [content, setContent] = useState(DEFAULT_LANDING_PAGE_CONTENT);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const next = await getLandingPageContentPublicBySlug(slug);
        if (!active) return;
        setContent(normalizeLandingPageContent(next));
      } catch (_err) {
        if (!active) return;
        setContent(DEFAULT_LANDING_PAGE_CONTENT);
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  useLandingSeo(content);

  const onTrack = useCallback((eventName, properties = {}) => {
    trackLandingEvent(eventName, properties);
  }, []);

  const normalizedContent = useMemo(() => normalizeLandingPageContent(content), [content]);
  const accentColor = normalizedContent.theme.accentColor;

  const sectionRenderers = useMemo(
    () => ({
      hero: (color) => (
        <HeroDashboardSection
          content={normalizedContent}
          accentColor={accentColor}
          sectionColor={color}
          onTrack={onTrack}
        />
      ),
      workflow: (color) => (
        <WorkflowSection
          content={normalizedContent}
          accentColor={accentColor}
          sectionColor={color}
          onTrack={onTrack}
        />
      ),
      caseStudy: (color) => (
        <CaseTimelineSection
          content={normalizedContent}
          accentColor={accentColor}
          sectionColor={color}
          onTrack={onTrack}
        />
      ),
      sectors: (color) => (
        <SectorSection
          content={normalizedContent}
          accentColor={accentColor}
          sectionColor={color}
          onTrack={onTrack}
        />
      ),
      testimonials: (color) => (
        <TestimonialsSection
          content={normalizedContent}
          accentColor={accentColor}
          sectionColor={color}
        />
      ),
      faq: (color) => (
        <FaqSection
          content={normalizedContent}
          accentColor={accentColor}
          sectionColor={color}
          onTrack={onTrack}
        />
      ),
      customBlocks: (color) => (
        <CustomBlocksSection
          content={normalizedContent}
          accentColor={accentColor}
          sectionColor={color}
          onTrack={onTrack}
        />
      ),
      finalCta: (color) => (
        <FinalCta
          content={normalizedContent}
          accentColor={accentColor}
          sectionColor={color}
          onTrack={onTrack}
        />
      ),
    }),
    [accentColor, normalizedContent, onTrack]
  );

  const orderedSections = normalizedContent.layout.sectionOrder
    .map((key) => {
      const render = sectionRenderers[key];
      if (!render) return null;
      return {
        key,
        node: render(normalizedContent.layout.sectionColors[key]),
      };
    })
    .filter(Boolean);

  return (
    <div
      className="min-h-screen bg-slate-50 text-slate-900"
      style={{ fontFamily: "'Manrope', 'Segoe UI', sans-serif" }}
    >
      <TopNav navigation={normalizedContent.navigation} accentColor={accentColor} onTrack={onTrack} />
      <main>{orderedSections.map((entry) => <React.Fragment key={entry.key}>{entry.node}</React.Fragment>)}</main>
      <LandingFooter footer={normalizedContent.footer} />
    </div>
  );
}
